import { ISchedule } from "@gettactic/api";
import { formatInTimeZone } from "date-fns-tz";

const toWallClock = (utcDateString: string, timeZone: string): string =>
  utcDateString
    ? `${formatInTimeZone(utcDateString, timeZone, "yyyy-MM-dd'T'HH:mm:ss")}.000Z`
    : utcDateString;

/**
 * Convert a schedule whose times are true UTC (e.g. the agenda endpoint) into
 * the office wall-clock "fake Z" convention returned by the schedules
 * endpoints, which the reserve/edit flows assume when they strip the "Z"
 * (dateStringToLocalTime / convertUTCToLocalDate) [sc-8721].
 *
 * Returns a copy — never mutates the (query-cached) input.
 *
 * `officeTimeZone` wins over the schedule's own `time_zone` and is what agenda
 * callers must pass: that endpoint reports `time_zone: "UTC"` — the convention
 * of its payload, not where the office is — so reading the zone off the
 * schedule would convert nothing at all.
 */
export const scheduleToOfficeWallClock = (
  schedule: ISchedule,
  officeTimeZone?: string
): ISchedule => {
  const timeZone = officeTimeZone || schedule.time_zone;
  if (!timeZone) {
    return schedule;
  }
  return {
    ...schedule,
    start: toWallClock(schedule.start, timeZone),
    end: toWallClock(schedule.end, timeZone),
    slots: schedule.slots.map((slot) => ({
      ...slot,
      start: toWallClock(slot.start, timeZone),
      end: toWallClock(slot.end, timeZone)
    }))
  };
};
